import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import useCockpitGalleryImages from '../../pages/product-detail/hooks/use-cockpit-gallery-images'
import {
    filterSetCombinationImages,
    getProductSetSelectionImageName
} from '../../utils/image-groups-utils'

/**
 * Builds the full gallery items list for XLEAP product gallery.
 *
 * The hook:
 * - selects the correct image group based on view type ("mainview") and selected variations
 * - includes c_extraMainImage before images from the image group
 * - appends c_extraMainImage2 after images from the image group
 * - inserts brightcove product videos at selected positions
 * - normalizes image assets (adds `type: 'image'` and resolves `src`)
 * - normalizes brightcove assets (maps `videoId` → `brightcoveId`)
 * - prepends the main product video (`c_main-video`) if present
 *
 * Returned items are ordered as:
 * 1. Main product video (if exists)
 * 2. Images and videos
 *
 * Differences compared to `useCockpitGalleryImages`:
 *
 * - Returns c_main-video (type: 'direct') as first and images and videos in their selected order.
 *
 *
 * @param {Object} product
 * @param {Object} product.imageGroups - Image groups from SFCC product
 * @param {Object} [product['c_main-video']] - Main product video asset
 *
 * @param {Object} derivedProduct
 * @param {Array<Object>} [derivedProduct.videos] - brightcove product videos
 * @param {Object} derivedProduct.variationParams - Selected variation attributes
 *
 * @returns {Array<Object>} Normalized gallery items ready for rendering
 */

const useXleapGalleryImages = (product, derivedProduct, stackedImage) => {
    const {formatMessage} = useIntl()
    const rawImagesAndVideos = useCockpitGalleryImages(product, derivedProduct)

    const selectionImageName = useMemo(
        () => (product?.setProducts?.length ? getProductSetSelectionImageName(product) : null),
        [product]
    )

    const imagesAndVideos = useMemo(
        () =>
            selectionImageName
                ? filterSetCombinationImages(product, rawImagesAndVideos, selectionImageName)
                : rawImagesAndVideos,
        [product, rawImagesAndVideos, selectionImageName]
    )

    const mainVideo = product?.['c_main-video']
    const productName = product?.name

    return useMemo(() => {
        const getVideoAlt = (videoCount) =>
            formatMessage(
                {
                    id: 'product_detail.media.video_alt',
                    defaultMessage: '{productName} Video {videoCount}'
                },
                {
                    productName,
                    videoCount
                }
            )

        let videoCount = mainVideo ? 1 : 0

        return [
            stackedImage?.isLoading
                ? {
                      type: 'stacked',
                      isLoading: true
                  }
                : stackedImage?.images?.length > 0 && {
                      type: 'stacked',
                      images: stackedImage.images,
                      alt: stackedImage.alt || productName || '',
                      isLoading: stackedImage.isLoading
                  },

            mainVideo && {
                ...mainVideo,
                type: 'direct',
                videoControls: false,
                showVideoInfo: true,
                alt: getVideoAlt(1)
            },

            ...imagesAndVideos.map((iv) => {
                const normalizedItem = {
                    ...iv,
                    // default to type image
                    type: iv.type || 'image',
                    // normalize the src
                    src: iv.src || iv.disBaseLink || iv.link
                }

                if (iv.type !== 'brightcove') {
                    return normalizedItem
                }

                videoCount += 1

                return {
                    ...normalizedItem,
                    videoControls: false,
                    showVideoInfo: true,
                    brightcoveId: iv.videoId,
                    poster: {
                        src: iv.poster,
                        alt: getVideoAlt(videoCount)
                    }
                }
            })
        ].filter(Boolean)
    }, [formatMessage, mainVideo, imagesAndVideos, stackedImage, productName])
}

export default useXleapGalleryImages
